import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "react-query";
import { getPublicProject } from "../Projects/services/projectCrud";
import { getPublicTree } from "../Trees/services/treeCrud";
import { getTodayWeather, getTommorowWeather } from "@helper/weather";
import useTreeRotation from "./hooks/useSlideRotation";
import "./ExternalDisplayV2.scss";

// =============================================================================
// Helpers
// =============================================================================

const WEATHER_ICONS = {
  0: "/img/weather/sun.png",
  1: "/img/weather/partly-sunny.png",
  2: "/img/weather/cloud.png",
  3: "/img/weather/cloud.png",
};

function WeatherIcon({ status, size = 22 }) {
  const src = WEATHER_ICONS[status] ?? WEATHER_ICONS[2];
  return (
    <img
      src={src}
      alt=""
      aria-hidden="true"
      style={{ width: size, height: size, objectFit: "contain" }}
    />
  );
}

function getFallbackTreeImage(species) {
  const name = (species?.de_name || species?.name || "").toLowerCase();
  if (name.includes("amber")) return "/img/trees/amberbaum.png";
  if (name.includes("eisen")) return "/img/trees/eisenholz.png";
  if (name.includes("bluet") || name.includes("blüten")) return "/img/trees/bluetensteppe.png";
  return "/img/trees/amberbaum.png";
}

// Map tree.health_state (numeric 0-4 or string) to a risk level.
function mapHealthToRisk(healthState) {
  if (healthState === null || healthState === undefined) return "unknown";
  if (typeof healthState === "number") {
    const map = { 0: "safe", 1: "lrisk", 2: "hrisk", 3: "unknown", 4: "frost" };
    return map[healthState] || "unknown";
  }
  if (typeof healthState !== "string") return "unknown";
  const n = healthState.toLowerCase();
  if (n.includes("good") || n.includes("gut")) return "safe";
  if (n.includes("high") || n.includes("stark")) return "hrisk";
  if (n.includes("medium") || n.includes("mittel")) return "mrisk";
  if (n.includes("low") || n.includes("leicht") || n.includes("dry")) return "lrisk";
  if (n.includes("frost")) return "frost";
  return "unknown";
}

const RISK_CONFIG = {
  safe: { color: "#3DB87A", label: "Gut versorgt", hint: "Der Baum ist\ngut versorgt." },
  lrisk: { color: "#F5C842", label: "Leicht trocken", hint: "Bewässerung\nbald empfohlen." },
  mrisk: { color: "#F0883E", label: "Trockenheit", hint: "Bewässerung\nheute empfohlen." },
  hrisk: { color: "#E05050", label: "Stark trocken", hint: "Akuter\nWasserbedarf." },
  frost: { color: "#2569A3", label: "Frostgefahr", hint: "Frostwarnung\naktiv." },
  unknown: { color: "#8AABBE", label: "Kein Status", hint: "Daten werden\ngeladen." },
};

function formatDate(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const days = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];
  return `${days[d.getDay()]}, ${String(d.getDate()).padStart(2, "0")}.${String(
    d.getMonth() + 1
  ).padStart(2, "0")}.${d.getFullYear()}`;
}

function getSensorInfo(tree) {
  const inst = tree?.installations?.[0];
  const s = inst?.sensor;
  if (!s) {
    return { label: "Messung offline", type: "crown" };
  }
  if (s.soil_moisture_sensor || s.soil_water_tension_sensor) {
    return { label: "FDR-Bodensensor aktiv", type: "soil" };
  }
  return { label: "Kronensensor aktiv", type: "crown" };
}

function getMetrics(tree) {
  const inst = tree?.installations?.[0];
  const s = inst?.sensor;
  const species = tree?.species;

  let moistureVal = null;
  let moistureLabel = "Kronenfeuchtigkeit";

  if (s?.pulse_sensor?.moisture_content != null) {
    moistureVal = Math.round(s.pulse_sensor.moisture_content);
    moistureLabel = "Kronenfeuchtigkeit";
  } else if (s?.soil_moisture_sensor?.soil_moisture != null) {
    moistureVal = Math.round(s.soil_moisture_sensor.soil_moisture);
    moistureLabel = "Bodenfeuchte";
  } else if (tree?.moisture_content != null) {
    moistureVal = Math.round(tree.moisture_content);
  }

  const tempVal =
    s?.pulse_sensor?.temperature != null
      ? Math.round(s.pulse_sensor.temperature)
      : s?.soil_moisture_sensor?.soil_temperature != null
      ? Math.round(s.soil_moisture_sensor.soil_temperature)
      : null;

  const waterReq = species?.water_requirement_per_day || null;

  return {
    moistureVal,
    moistureLabel,
    tempVal,
    waterReq,
  };
}

// =============================================================================
// Left panel (static content + live clock)
// =============================================================================

function LeftPanel({ project }) {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const HH = String(now.getHours()).padStart(2, "0");
  const MM = String(now.getMinutes()).padStart(2, "0");
  const days = ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"];
  const months = [
    "Jan",
    "Feb",
    "Mär",
    "Apr",
    "Mai",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Okt",
    "Nov",
    "Dez",
  ];
  const dateStr = `${days[now.getDay()]}, ${now.getDate()}. ${
    months[now.getMonth()]
  } ${now.getFullYear()}`;

  const projectName = project?.name || "Friedrichshafener Straße";
  const projectDescription =
    project?.description ||
    'Im Rahmen des Städtebauförderungsprogramms „Sozialer Zusammenhalt" werden hier neue Stadtbäume gepflanzt. Einige davon tragen Sensoren in der Baumkrone. Die Messdaten helfen, die Bäume gesund aufwachsen zu lassen und machen Klimawandel erlebbar.';

  return (
    <div className="dv2-left">
      <div>
        <div className="dv2-brand-row">
          <div className="dv2-brand-dot" />
          <div className="dv2-brand-name">TreeSense · Live-Monitoring</div>
        </div>
        <div className="dv2-location-tag">{projectName} · München</div>
      </div>

      <div>
        <div className="dv2-hero-title">
          Unsere Bäume
          <br />
          <span>sprechen mit uns.</span>
        </div>
        <div className="dv2-hero-sub">
          Sensoren in den Bäumen messen die Feuchtigkeit in Echtzeit. So wissen die Gärtner genau,
          wann ein Baum Wasser braucht – kein Rätselraten mehr.
        </div>
      </div>

      <div className="dv2-hr" />

      <div>
        <div className="dv2-slabel">Wussten Sie schon?</div>
        <div className="dv2-facts">
          <div className="dv2-fact">
            <img src="/img/weather/sun.png" alt="" aria-hidden="true" className="dv2-fact-icon" />
            <div className="dv2-fact-body">
              <strong>Bis zu 8 °C kühler</strong>
              Ein einziger Baum kühlt seine Umgebung um bis zu 8 Grad – wie eine natürliche
              Klimaanlage.
            </div>
          </div>
          <div className="dv2-fact">
            <img
              src="/img/weather/watering.png"
              alt=""
              aria-hidden="true"
              className="dv2-fact-icon"
            />
            <div className="dv2-fact-body">
              <strong>70 % weniger Wasserverbrauch</strong>
              Datengestützte Bewässerung spart gegenüber Pauschalbewässerung bis zu 70 % Wasser.
            </div>
          </div>
          <div className="dv2-fact">
            <img
              src="/img/weather/trees-leaves.png"
              alt=""
              aria-hidden="true"
              className="dv2-fact-icon"
            />
            <div className="dv2-fact-body">
              <strong>Heimat für Insekten</strong>
              Stadtbäume bieten Lebensraum für Bienen, Hummeln und Schmetterlinge.
            </div>
          </div>
        </div>
      </div>

      <div className="dv2-hr" />

      <div className="dv2-projekt-box">
        <div className="dv2-slabel" style={{ marginBottom: 8 }}>
          Über das Projekt
        </div>
        <p>{projectDescription}</p>
      </div>

      <div className="dv2-clock-wrap">
        <div className="dv2-clock-time">
          {HH}:{MM}
        </div>
        <div className="dv2-clock-date">{dateStr}</div>
      </div>
    </div>
  );
}

// =============================================================================
// Slide (right panel card for a single tree)
// =============================================================================

function Slide({ tree, weather, isActive, slideIndex, totalSlides, progressPct, onGoTo }) {
  const species = tree?.species;
  const treeName = tree?.name || species?.de_name || species?.name || "Baum";
  const treeLatin = species?.name && species?.name !== treeName ? species.name : null;
  const sensorInfo = getSensorInfo(tree);
  const risk = mapHealthToRisk(tree?.health_state);
  const riskCfg = RISK_CONFIG[risk];
  const { moistureVal, moistureLabel, tempVal, waterReq } = getMetrics(tree);

  const maintenances = tree?.maintenances || [];
  const now = new Date();
  const past = [...maintenances]
    .filter((m) => new Date(m.date) <= now)
    .sort((a, b) => new Date(b.date) - new Date(a.date))[0];
  const next = [...maintenances]
    .filter((m) => new Date(m.date) > now)
    .sort((a, b) => new Date(a.date) - new Date(b.date))[0];

  const steckbrief =
    species?.description ||
    (species?.height && species?.crown_width
      ? `${treeName} wird ${species.height} hoch und ${species.crown_width} breit. ${
          species?.root_type ? `Wurzeltyp: ${species.root_type}.` : ""
        }`.trim()
      : "Dieser Baum wird regelmäßig überwacht. Die Sensordaten helfen den Gärtnern dabei, ihn gesund wachsen zu lassen.");

  const moisturePct = moistureVal != null ? Math.max(0, Math.min(100, moistureVal)) : 0;
  const tempPct = tempVal != null ? Math.max(0, Math.min(100, (tempVal / 35) * 100)) : 0;

  // Gauge circle math
  const circumference = 2 * Math.PI * 48; // ~301
  const gaugeOffset = circumference * (1 - moisturePct / 100);

  const todayStatus = weather ? getTodayWeather(weather) : 2;
  const tomorrowStatus = weather ? getTommorowWeather(weather) : 2;

  return (
    <div className={`dv2-slide dv2-slide--${risk} ${isActive ? "dv2-slide--active" : ""}`}>
      <div className="dv2-slide-header">
        <div className={`dv2-sensor-badge dv2-sensor-badge--${sensorInfo.type}`}>
          <span className="dv2-s-dot" />
          {sensorInfo.label}
        </div>
        <div className="dv2-nav-dots">
          {Array.from({ length: totalSlides }).map((_, i) => (
            <button
              key={i}
              type="button"
              className={`dv2-nd ${i === slideIndex ? "dv2-nd--on" : ""}`}
              onClick={() => onGoTo(i)}
              aria-label={`Baum ${i + 1}`}
            />
          ))}
        </div>
      </div>

      <div className="dv2-slide-body">
        <div className="dv2-s-left">
          <div>
            <div className="dv2-tree-name">{treeName}</div>
            {(treeLatin || sensorInfo.label) && (
              <div className="dv2-tree-species">
                {treeLatin && <em>{treeLatin}</em>}
                {treeLatin && " · "}
                {sensorInfo.label}
              </div>
            )}
          </div>

          <div className="dv2-steckbrief">{steckbrief}</div>

          <div className="dv2-metrics">
            <div className="dv2-mcard">
              <div className="dv2-mlabel">{moistureLabel}</div>
              <div className="dv2-mval">
                {moistureVal != null ? moistureVal : "–"}
                <span className="dv2-munit">%</span>
              </div>
              <div className="dv2-mbar">
                <div className="dv2-mbar-fill" style={{ width: `${moisturePct}%` }} />
              </div>
            </div>
            <div className="dv2-mcard">
              <div className="dv2-mlabel">Temperatur</div>
              <div className="dv2-mval">
                {tempVal != null ? tempVal : "–"}
                <span className="dv2-munit">°C</span>
              </div>
              <div className="dv2-mbar">
                <div className="dv2-mbar-fill" style={{ width: `${tempPct}%` }} />
              </div>
            </div>
            <div className="dv2-mcard">
              <div className="dv2-mlabel">Wasserbedarf</div>
              <div className="dv2-mval" style={{ fontSize: 20, lineHeight: 1.3 }}>
                {waterReq ? waterReq : "50–80"}
                <span className="dv2-munit"> L</span>
              </div>
              <div className="dv2-mbar">
                <div className="dv2-mbar-fill" style={{ width: "50%" }} />
              </div>
            </div>
          </div>

          <div className="dv2-weather">
            <div className="dv2-wi">
              <WeatherIcon status={todayStatus} size={24} />
              <div>
                <div className="dv2-wi-val">
                  {weather?.temperature?.today != null ? `${weather.temperature.today} °C` : "–"}
                </div>
                <div className="dv2-wi-lbl">Heute</div>
              </div>
            </div>
            <div className="dv2-wi">
              <WeatherIcon status={tomorrowStatus} size={24} />
              <div>
                <div className="dv2-wi-val">
                  {weather?.temperature?.tomorrow != null
                    ? `${weather.temperature.tomorrow} °C`
                    : "–"}
                </div>
                <div className="dv2-wi-lbl">Morgen</div>
              </div>
            </div>
            <div className="dv2-wi">
              <img
                src="/img/weather/watering.png"
                alt=""
                aria-hidden="true"
                style={{ width: 24, height: 24, objectFit: "contain" }}
              />
              <div>
                <div className="dv2-wi-val">
                  {weather?.rain?.today != null ? `${weather.rain.today} mm` : "–"}
                </div>
                <div className="dv2-wi-lbl">Niederschlag</div>
              </div>
            </div>
          </div>

          <div className="dv2-pflege">
            <div className="dv2-pcard">
              <div className="dv2-plbl">Letzter Pflegegang</div>
              <div className="dv2-pval">{formatDate(past?.date) || "—"}</div>
            </div>
            <div className="dv2-pcard">
              <div className="dv2-plbl">Nächster Pflegegang</div>
              <div className="dv2-pval">{formatDate(next?.date) || "—"}</div>
            </div>
          </div>
        </div>

        <div className="dv2-s-right">
          <div
            className="dv2-status-ring"
            style={{ borderColor: riskCfg.color, background: `${riskCfg.color}22` }}
          >
            <span className="dv2-status-ring__pulse" style={{ background: riskCfg.color }} />
            <img
              src={getFallbackTreeImage(species)}
              alt=""
              aria-hidden="true"
              className="dv2-status-ring__img"
            />
          </div>
          <div className="dv2-status-label" style={{ color: riskCfg.color }}>
            {riskCfg.label}
          </div>
          <div className="dv2-status-hint">
            {riskCfg.hint.split("\n").map((line, i) => (
              <React.Fragment key={i}>
                {line}
                <br />
              </React.Fragment>
            ))}
          </div>

          <div className="dv2-gauge-wrap">
            <div className="dv2-gauge-title">Wasserversorgung</div>
            <svg width="124" height="124" viewBox="0 0 116 116">
              <circle
                cx="58"
                cy="58"
                r="48"
                fill="none"
                stroke="rgba(14, 29, 44, 0.08)"
                strokeWidth="9"
              />
              <circle
                cx="58"
                cy="58"
                r="48"
                fill="none"
                stroke={riskCfg.color}
                strokeWidth="9"
                strokeDasharray={circumference}
                strokeDashoffset={gaugeOffset}
                strokeLinecap="round"
                transform="rotate(-90 58 58)"
                style={{ transition: "stroke-dashoffset 0.6s ease" }}
              />
              <text
                x="58"
                y="56"
                textAnchor="middle"
                fill="#0E1D2C"
                fontFamily="Syne, sans-serif"
                fontSize="22"
                fontWeight="700"
              >
                {moistureVal != null ? `${moistureVal}%` : "–"}
              </text>
              <text x="58" y="72" textAnchor="middle" fill="#6B8FA4" fontSize="10">
                Feuchtigkeit
              </text>
            </svg>
          </div>
        </div>
      </div>

      <div className="dv2-prog-bar" style={{ width: `${progressPct}%` }} />
    </div>
  );
}

// =============================================================================
// Footer (logos)
// =============================================================================

function Footer() {
  return (
    <footer className="dv2-footer">
      <div className="dv2-footer__left">
        <img
          src="/img/logos/staedtebau-hinweis.png"
          alt="Gefördert im Städtebauförderungsprogramm Sozialer Zusammenhalt"
          className="dv2-footer__banner"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      </div>
      <div className="dv2-footer__right">
        <img
          src="/img/logos/mgs.png"
          alt="Münchner Gesellschaft für Stadterneuerung"
          className="dv2-footer__mgs"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
        <div className="dv2-footer__treesense">
          <img
            src="/img/treesense-logo.webp"
            alt="TreeSense"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        </div>
      </div>
    </footer>
  );
}

// =============================================================================
// Main component
// =============================================================================

export default function ExternalDisplayV2() {
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  const interval = parseInt(searchParams.get("interval") || "12000", 10);

  const { data: project, isLoading: projectLoading } = useQuery(
    ["display-v2", "project", projectId],
    () => getPublicProject(projectId),
    {
      refetchInterval: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      staleTime: 2 * 60 * 1000,
      keepPreviousData: true,
      retry: 3,
      retryDelay: 10000,
    }
  );

  const trees = useMemo(() => {
    if (!project?.areas) return [];
    return project.areas.flatMap((area) => area.tree_set || []);
  }, [project]);
  const treeCount = trees.length || 1;
  const { currentTreeIndex, goToIndex } = useTreeRotation(treeCount, interval);
  const currentTreeId = trees[currentTreeIndex]?.id;

  const { data: tree } = useQuery(
    ["display-v2", "tree", currentTreeId],
    () => getPublicTree(currentTreeId),
    {
      enabled: !!currentTreeId,
      refetchInterval: 5 * 60 * 1000,
      staleTime: 2 * 60 * 1000,
      keepPreviousData: true,
      retry: 3,
      retryDelay: 10000,
    }
  );

  // Progress bar animation for the currently active slide
  const [progressPct, setProgressPct] = useState(0);
  useEffect(() => {
    setProgressPct(0);
    const start = Date.now();
    const timer = setInterval(() => {
      const pct = Math.min(((Date.now() - start) / interval) * 100, 100);
      setProgressPct(pct);
      if (pct >= 100) clearInterval(timer);
    }, 50);
    return () => clearInterval(timer);
  }, [currentTreeIndex, interval]);

  if (!project && projectLoading) {
    return <div className="dv2-loading">Daten werden geladen…</div>;
  }

  const weather = project?.weather;

  return (
    <div className="dv2-screen">
      <div className="dv2-main">
        <LeftPanel project={project} />

        <div className="dv2-right">
          <Slide
            tree={tree}
            weather={weather}
            isActive
            slideIndex={currentTreeIndex}
            totalSlides={treeCount}
            progressPct={progressPct}
            onGoTo={goToIndex}
          />
        </div>
      </div>

      <Footer />
    </div>
  );
}
